import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { studentActions } from "../slices/student";
import { StudentMapper } from "../../mappers/StudentMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { StudentCreateRequestDto } from "../../shared/dtos/student/StudentCreateRequestDto";
import { StudentResponseDto } from "../../shared/dtos/student/StudentResponseDto";
import { StudentSummaryDto } from "../../shared/dtos/student/StudentSummaryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { UpdateStudentPayload } from "../../shared/interfaces/student/UpdateStudentPayload";

export const createStudent =
  (values: StudentCreateRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(studentActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<StudentResponseDto>
      >("/students", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Student created successfully!");
        const listStudentData = StudentMapper.mapStudentToSummary(data.data);
        dispatch(studentActions.createSuccess(listStudentData));
        dispatch(
          studentActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(studentActions.setErrorCreate(message));
    }
  };

export interface StudentQueryParams extends PaginationQueryDto {
  branchId?: string;
}

export const getStudents =
  (params: StudentQueryParams = { page: 1, size: 10 }) =>
  async (dispatch: AppDispatch) => {
    dispatch(studentActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<StudentSummaryDto>>
      >("/students", { headers: { requiresAuth: true }, params });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(studentActions.setItems(data.data.content));
        dispatch(studentActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(studentActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getStudentById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(studentActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<StudentResponseDto>
    >(`/students/${id}`, { headers: { requiresAuth: true } });

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(studentActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(studentActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const updateStudent =
  (payload: UpdateStudentPayload) => async (dispatch: AppDispatch) => {
    dispatch(studentActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<StudentResponseDto>
      >(`/students/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Student updated successfully!");
        const listStudentData = StudentMapper.mapStudentToSummary(data.data);
        dispatch(studentActions.updateSuccess(listStudentData));
        dispatch(
          studentActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(studentActions.setErrorUpdate(message));
    }
  };

export const deleteStudent = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(studentActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/students/${id}`,
      { headers: { requiresAuth: true } },
    );

    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Student deleted successfully!");
      dispatch(studentActions.deleteSuccess(id));
      dispatch(
        studentActions.setServerStatus({
          operation: "delete",
          status: data.statusCode || 200,
        }),
      );
    }
  } catch (error) {
    const message = getErrorMessage(error);
    toast.error(message);
    dispatch(studentActions.setErrorDelete(message));
  }
};

export const resetStudentServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(studentActions.setServerStatus(null));
};
